import styles from './GridModule.module.scss';

import cx from 'classnames';
import type { ReferencedEntry } from 'contentful';
import React from 'react';

import type { GridAssembly } from 'api/contentful/contentTypes.cloverdotcom';

import { componentKey, getField } from 'utils/EntryUtil';
import { FadeToastUp } from '../Animation/FadeToastUp/FadeToastUp';
import { applyEntryDataAttributes } from '../EntryWrapper/EntryDataWrapper';
import { GridCell } from './GridCell';

import { ContentLink } from '../ContentLink/ContentLink';
import { Blurb } from '../Contentful/Blurb';
import { Text } from '../Contentful/Text';

interface IGridModuleProps extends React.AllHTMLAttributes<HTMLElement> {
  entry?: ReferencedEntry<GridAssembly>;
}

export const GridModule: React.FC<IGridModuleProps> = ({ entry, className, ...otherProps }) => {
  const items = getField(entry, 'items');
  const topic = getField(entry, 'topic');
  const link = getField(topic, 'link');
  const title = getField(entry, 'title');
  const blurb = getField(topic, 'blurb');
  const headingTagName = getField(entry, 'headingTagName', 'h2');

  if (!items) return null;

  return (
    <section className={cx(styles.moduleWrapper, className)} {...otherProps}>
      <FadeToastUp threshold={0.1}>
        <div className={cx(styles.gridModule, items.length % 4 === 0 ? styles.gridModuleFourCol : null)}>
          {(title || blurb) && (
            <div className={styles.textWrapper}>
              <Text tagName={headingTagName} entry={topic || entry} field="title" className={styles.title} />
              <Blurb entry={topic} field="blurb" />
            </div>
          )}
          <div className={styles.grid}>
            {items.map(
              (item, idx) =>
                item && (
                  <FadeToastUp key={componentKey(item, idx)} delayMultiplier={idx + 1}>
                    {({ className, style, ref }) => (
                      <GridCell
                        entry={item}
                        style={style}
                        innerRef={ref as React.Ref<HTMLDivElement>}
                        className={cx(className, styles.cell)}
                      />
                    )}
                  </FadeToastUp>
                ),
            )}
          </div>
          <ContentLink entry={link} />
        </div>
      </FadeToastUp>
    </section>
  );
};

const WrappedGridModule = applyEntryDataAttributes<GridAssembly>(GridModule);

export { WrappedGridModule };

export default WrappedGridModule;
